<template>
  <section class="sp-panel sp-reader">
    <button type="button" class="sp-link" @click="close">
      ← Back to requests
    </button>
    <portal-feedback
      :loading="loading"
      :error="error"
      :retry="!request"
      :success="success"
      @retry="load"
    />
    <template v-if="request">
      <div class="sp-row sp-between">
        <div>
          <p class="sp-eyebrow">
            HR request #{{ request.id }} · {{ categoryLabel(request.category) }}
          </p>
          <h2>{{ request.subject }}</h2>
        </div>
        <span class="sp-badge">{{ statusLabel(request.status) }}</span>
      </div>
      <p class="sp-notice">
        This conversation is visible to {{ request.ownerName }} and the assigned
        HR handler, {{ request.handlerName }}.
      </p>
      <dl class="sp-metadata">
        <div>
          <dt>Submitted by</dt>
          <dd>{{ request.ownerName }}</dd>
        </div>
        <div>
          <dt>HR handler</dt>
          <dd>{{ request.handlerName }}</dd>
        </div>
        <div>
          <dt>Submitted</dt>
          <dd>{{ displayDate(request.createdAt) }}</dd>
        </div>
        <div v-if="request.occurredOn">
          <dt>Date of occurrence</dt>
          <dd>{{ displayDate(request.occurredOn) }}</dd>
        </div>
        <div v-if="request.reviewDueDate">
          <dt>Initial review target</dt>
          <dd>{{ displayDate(request.reviewDueDate) }}</dd>
        </div>
        <div v-if="request.resolutionTargetDate">
          <dt>Resolution target</dt>
          <dd>{{ displayDate(request.resolutionTargetDate) }}</dd>
        </div>
      </dl>
      <p v-if="request.reviewDueDate" class="sp-muted">
        These are target dates calculated in business days, excluding company
        holidays. HR will keep you informed about progress.
      </p>
      <h3>Request details</h3>
      <p class="sp-prose">{{ request.description }}</p>
      <template v-if="request.requestedResolution"
        ><h3>Requested resolution</h3>
        <p class="sp-prose">{{ request.requestedResolution }}</p></template
      >
      <section class="sp-question">
        <h3>Conversation</h3>
        <p v-if="!request.messages.length" class="sp-muted">
          No replies yet. New replies will appear here.
        </p>
        <article
          v-for="entry in request.messages"
          :key="entry.id"
          class="sp-message"
        >
          <p class="sp-message-meta">
            <strong>{{ entry.authorName }}</strong> ·
            {{ entry.authorRole === 'handler' ? 'HR' : 'Staff' }} ·
            {{ displayDate(entry.createdAt) }}
          </p>
          <p class="sp-prose">{{ entry.body }}</p>
        </article>
      </section>
      <form
        v-if="can('reply')"
        class="sp-question"
        @submit.prevent="act('reply')"
      >
        <label class="sp-field"
          >Reply to this conversation<textarea
            v-model="message"
            maxlength="10000"
            required
            :disabled="busy"
            @input="dirty = true"
          /><small
            >{{ message.length }}/10000 characters · Your reply is shared within
            this request.</small
          ></label
        ><oxd-button
          type="submit"
          label="Post reply"
          display-type="secondary"
          :disabled="busy || !message.trim()"
        />
      </form>
      <form
        v-if="can('status')"
        class="sp-notice"
        @submit.prevent="act('status')"
      >
        <h3>HR status update</h3>
        <fieldset :disabled="busy">
          <label class="sp-field"
            >New status<select
              v-model="nextStatus"
              required
              @change="dirty = true"
            >
              <option value="">Select a status</option>
              <option
                v-for="status in statusOptions"
                :key="status"
                :value="status"
                :disabled="status === request.status"
              >
                {{ statusLabel(status) }}
              </option>
            </select></label
          ><label class="sp-field"
            >Message to staff{{
              nextStatus === 'resolved' ? ' (required)' : ' (optional)'
            }}<textarea
              v-model="statusMessage"
              maxlength="10000"
              :required="nextStatus === 'resolved'"
              @input="dirty = true"
            /></label
          ><oxd-button
            type="submit"
            label="Update request status"
            display-type="secondary"
            :disabled="
              busy ||
              !nextStatus ||
              nextStatus === request.status ||
              (nextStatus === 'resolved' && !statusMessage.trim())
            "
          />
        </fieldset>
      </form>
      <form
        v-if="can('withdraw')"
        class="sp-question"
        @submit.prevent="act('withdraw')"
      >
        <details>
          <summary>Withdraw this request</summary>
          <p class="sp-muted">
            The request and its conversation will remain in your history.
          </p>
          <label class="sp-field"
            >Reason (optional)<textarea
              v-model="withdrawMessage"
              maxlength="10000"
              :disabled="busy"
              @input="dirty = true"
            /></label
          ><oxd-button
            type="submit"
            label="Withdraw request"
            display-type="danger"
            :disabled="busy"
          />
        </details>
      </form>
      <p
        v-if="['resolved', 'withdrawn'].includes(request.status)"
        class="sp-notice"
      >
        This request is closed. Start a new HR request if you need further
        assistance.
      </p>
      <details
        v-if="request.history && request.history.length"
        class="sp-question"
      >
        <summary>Request activity</summary>
        <ol class="sp-history">
          <li v-for="(event, index) in request.history" :key="index">
            {{ actionLabel(event.action) }} · {{ event.actorName }} ·
            {{ displayDate(event.at) }}
          </li>
        </ol>
      </details>
      <div class="sp-actions">
        <button type="button" class="sp-link" :disabled="busy" @click="reload">
          Reload latest conversation</button
        ><span v-if="dirty" class="sp-muted">You have unsaved text.</span>
      </div>
    </template>
  </section>
</template>

<script>
import {ref, onMounted} from 'vue';
import PortalFeedback from './PortalFeedback.vue';
import {
  portalApi,
  errorMessage,
  displayDate,
  statusLabel,
  useDraftGuard,
} from '../portal';
export default {
  components: {PortalFeedback},
  props: {
    requestId: {type: Number, required: true},
    categories: {type: Array, default: () => []},
  },
  emits: ['close', 'changed'],
  setup(props, {emit}) {
    const api = portalApi('requests');
    const request = ref(null),
      loading = ref(false),
      busy = ref(false),
      error = ref(''),
      success = ref(''),
      dirty = ref(false);
    const message = ref(''),
      statusMessage = ref(''),
      nextStatus = ref(''),
      withdrawMessage = ref('');
    const {canLeave} = useDraftGuard(dirty);
    const statusOptions = ['in_review', 'waiting_on_staff', 'resolved'];
    const can = (action) => Boolean(request.value?.actions?.includes(action));
    const categoryLabel = (id) =>
      props.categories.find((category) => category.id === id)?.label ||
      id.replace(/_/g, ' ');
    const actionLabel = (action) =>
      ({
        created: 'Request submitted',
        submit: 'Request submitted',
        reply: 'Reply posted',
        status: 'Status updated',
        withdraw: 'Request withdrawn',
      }[action] || action.replace(/_/g, ' '));
    function applyRequest(data) {
      request.value = data;
      message.value = '';
      statusMessage.value = '';
      nextStatus.value = '';
      withdrawMessage.value = '';
      dirty.value = false;
    }
    async function load() {
      loading.value = true;
      error.value = '';
      success.value = '';
      request.value = null;
      try {
        applyRequest(await api.get(props.requestId));
      } catch (e) {
        error.value = errorMessage(e);
      } finally {
        loading.value = false;
      }
    }
    function close() {
      if (!busy.value && canLeave()) {
        dirty.value = false;
        emit('close');
      }
    }
    function reload() {
      if (canLeave()) {
        dirty.value = false;
        load();
      }
    }
    async function act(action) {
      if (busy.value || !can(action)) return;
      error.value = '';
      success.value = '';
      const payload = {action, version: request.value.version};
      if (action === 'reply') {
        if (!message.value.trim()) return;
        payload.message = message.value.trim();
      }
      if (action === 'status') {
        if (
          !statusOptions.includes(nextStatus.value) ||
          nextStatus.value === request.value.status
        )
          return;
        if (nextStatus.value === 'resolved' && !statusMessage.value.trim()) {
          error.value =
            'Add a resolution message before resolving this request.';
          return;
        }
        payload.status = nextStatus.value;
        if (statusMessage.value.trim())
          payload.message = statusMessage.value.trim();
        if (
          nextStatus.value === 'resolved' &&
          !window.confirm(
            'Resolve this request? The conversation will become read-only.',
          )
        )
          return;
      }
      if (action === 'withdraw') {
        if (
          !window.confirm(
            'Withdraw this request? It will remain in your history and the conversation will become read-only.',
          )
        )
          return;
        if (withdrawMessage.value.trim())
          payload.message = withdrawMessage.value.trim();
      }
      busy.value = true;
      try {
        applyRequest(await api.act(request.value.id, payload));
        success.value =
          action === 'reply'
            ? 'Your reply has been posted.'
            : action === 'withdraw'
            ? 'Your request has been withdrawn.'
            : 'The request status has been updated.';
        emit('changed');
      } catch (e) {
        error.value = errorMessage(e);
      } finally {
        busy.value = false;
      }
    }
    onMounted(load);
    return {
      request,
      loading,
      busy,
      error,
      success,
      dirty,
      message,
      statusMessage,
      nextStatus,
      withdrawMessage,
      statusOptions,
      can,
      categoryLabel,
      actionLabel,
      displayDate,
      statusLabel,
      load,
      reload,
      close,
      act,
    };
  },
};
</script>
